import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';

import { Button } from '@wajeez-web/components/ui';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DeleteItemAlert } from '@wajeez-web/components/ui';
import { PlansActions } from '@wajeez-web/stores/product';
import { SuperAdminSystemPermissions } from '@wajeez-web/core/types';
import { PlusIcon, DownloadIcon } from '@heroicons/react/solid';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { Content, Server } from '@wajeez-web/core/api-base';
import {
  useContentSelector,
  ContentEntityActions,
  ContentEntityAudiosActions,
  useContentDispatch,
  clearData,
} from '@wajeez-web/stores/content';
import {
  useCommonDispatch,
  CommonActions,
  AlertType,
  useCommonSelector,
} from '@wajeez-web/stores/common';
import { CheckBoxType } from '@wajeez-web/components/mui';
import { DataTableWrapper } from '@wajeez-web/components/helpers';

import PublishAllCollectionComponentsModal from '../../components/PublishAllCollectionComponentsModal';
import AssignAuthorsModal from '../../components/AssignAuthorsModal';
import AssignPlansModal from '../../components/AssignPlansModal';

const ContentEntitiesPage = () => {
  const { type } = useParams<{ type: string }>();

  useEffect(() => {
    clearData();
  }, [type]);

  return <ContentEntitiesPageContent key={type} type={type} />;
};

interface ContentEntitiesPageContentProps {
  type: string | undefined;
}

const ContentEntitiesPageContent = ({
  type,
}: ContentEntitiesPageContentProps) => {
  const exportDataRef = useRef<any>(null);

  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();
  const commonDispatch = useCommonDispatch();

  const languageId = useCommonSelector(
    ({ common }) => common.settings.LanguageId
  );

  const [multiplePublishModalState, setMultiplePublish] = useState({
    open: false,
    selectedId: '',
  });
  const [plansModalState, setplansModalState] = useState({
    open: false,
    selected: '',
  });
  const [authorModalState, setAuthorModalState] = useState({
    open: false,
    selected: '',
  });
  const [isTypeChanging, setIsTypeChanging] = useState(false);

  const lookups = useContentSelector(
    ({ contentEntityType }) => contentEntityType.lookups
  );

  const data = useContentSelector(({ contentEntity }) => contentEntity.data);
  const status = useContentSelector(
    ({ contentEntity }) => contentEntity.status
  );
  const exportedData = useContentSelector(
    ({ contentEntity }) => contentEntity.exportedData
  );
  const updatePlansStatus = useContentSelector(
    ({ contentEntity }) => contentEntity.updatePlansStatus
  );
  const multiblePubliLoading = useContentSelector(
    (s) => s.contentEntity.multiblePublishStatus === 'loading'
  );

  const { t } = useTranslation();

  const {
    setTablePreferences,
    tablePreferances,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'content' + type,
    ContentEntityActions.getContentEntitiesAsDataTableAsync,
    [{ cretiria: type, comparisonType: 0, property: 2 }],
    undefined,
    [{ property: 524288, orderByDirection: 1 }]
    // data?.type === type ? data?.currentPage : 1
  );

  const onExport = async (flags?: number) => {
    if (data) {
      const res = await contentDispatch(
        ContentEntityActions.getContentEntitiesToBeExportedAsync({
          ...tablePreferances,
          pageNumber: 1,
          pageSize: data.totalCount,
        })
      );
      if (res.meta.requestStatus === 'fulfilled') {
        exportDataRef.current.link.click();
      }
    }
  };

  const getExportedData = () => {
    const d = [];
    if (data?.totalCount && data?.totalCount > 0) {
      for (let i = 0; i < data?.totalCount; i++) {
        const row: { [key: string]: any } = {};
        exportedData?.['columns']?.forEach((item: any) => {
          if (!item.isHidden) {
            row[item.header] = item.cells[i];
          }
        });

        d.push(row);
      }
    }
    return d;
  };

  useEffect(() => {
    if (data && data?.type !== type) {
      setIsTypeChanging(true);

      setTimeout(() => {
        setIsTypeChanging(false);
      }, 150);

      setTablePreferences(null);
    }
  }, [type]);

  return (
    <div className="h-full">
      <CSVLink
        filename={
          lookups.find((item) => item.name === type)?.displayNamePlural + '.csv'
        }
        data={getExportedData()}
        ref={exportDataRef}
        className="hidden"
      >
        Download me
      </CSVLink>
      <PageLayoutsContainer
        title={`${
          lookups.find((item) => item.name === type)?.displayNamePlural
        }`}
        endSideComponent={[
          {
            element: (
              <Button
                title={`${t('buttons.Add')} ${
                  lookups.find((item) => item.name === type)
                    ?.displayNameSingular
                }`}
                Icon={PlusIcon}
                onClick={() => {
                  navigate(`create/0`);
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [SuperAdminSystemPermissions.contentEntities.add],
          },
        ]}
        breadCrumbsLinks={[
          { title: 'Content-Entity', link: '/content-entity' },
          { title: type || '' },
        ]}
      >
        <div className="h-full">
          {!isTypeChanging ? (
            <DataTableWrapper
              data={data}
              onHeaderManage={(header) => {
                const flags = header
                  ?.filter((h) => h.selected)
                  .reduce(
                    (previousValue: number, currentValue: CheckBoxType) =>
                      previousValue + Number(currentValue.value),
                    0
                  );

                tablePreferances &&
                  flags &&
                  setTablePreferences({
                    ...tablePreferances,
                    flags: flags + 1,
                  });
              }}
              onPaginationChange={(pagination) => {
                tablePreferances &&
                  setTablePreferences({
                    ...tablePreferances,
                    pageNumber: pagination.pageNumber,
                    pageSize: pagination.pageSize,
                  });
              }}
              sort={tablePreferances?.sortingOperations?.[0]}
              onSort={(sort) => {
                tablePreferances &&
                  setTablePreferences({
                    ...tablePreferances,
                    sortingOperations: [sort],
                  });
              }}
              onFilter={(filters, pagination) => {
                const filtersObj = filters.reduce((prev, curr) => {
                  prev[Number(curr.key)] = {
                    property: Number(curr.key),
                    cretiria: curr.value,
                    comparisonType: curr.operator,
                  };

                  return prev;
                }, {} as { [key: number]: Server.FilteringOperationOfInteger });

                const tablePreferenceFiltersObj =
                  tablePreferances?.filteringOperations.reduce((prev, curr) => {
                    prev[Number(curr.property)] = curr;

                    return prev;
                  }, {} as { [key: number]: Server.FilteringOperationOfInteger });

                const finalFiltersObj = {
                  ...tablePreferenceFiltersObj,
                  ...filtersObj,
                };
                const finalFilters = Object.values(finalFiltersObj);

                const submittedFilters = [];

                for (const index in finalFilters) {
                  if (
                    index === '0' ||
                    filters.find(
                      (f) => Number(f.key) === finalFilters[index].property
                    )
                  ) {
                    submittedFilters.push(finalFilters[index]);
                  }
                }

                tablePreferances &&
                  setTablePreferences({
                    ...tablePreferances,
                    pageNumber: pagination.pageNumber,
                    filteringOperations: submittedFilters,
                  });
              }}
              isLoading={status === 'loading'}
              rowActions={(row) => {
                return [
                  {
                    title: 'Edit',
                    permission:
                      SuperAdminSystemPermissions.contentEntities.edit,
                    endpoint: ``,
                    type: 'request',
                    action: () => {
                      navigate(`${row[1]}/0`);
                    },
                  },
                  {
                    title:
                      row[256] === 'True'
                        ? 'Unpublish Content'
                        : 'Publish Content',
                    endpoint: '',
                    action: () => {
                      (row[256] === 'True'
                        ? contentDispatch(
                            ContentEntityActions.setPublishAsync({
                              id: row[1] as string,
                              published: false,
                              languageId: languageId,
                            })
                          ).then(() => {
                            getData();
                          })
                        : contentDispatch(
                            ContentEntityActions.setPublishAsync({
                              id: row[1] as string,
                              published: true,
                              languageId: languageId,
                            })
                          )
                      ).then(() => {
                        getData();
                      });
                    },
                    permission:
                      SuperAdminSystemPermissions.contentEntities.publish,
                    type: 'request',
                  },
                  {
                    title: 'Download',
                    endpoint: '',
                    permission:
                      SuperAdminSystemPermissions.contentEntities.download,
                    type: 'request',
                    action: () => {
                      contentDispatch(
                        ContentEntityAudiosActions.downloadAudioAsync({
                          id: row[1] as string,
                          name: row[
                            Content.ContentEntityLookUpColumns.Title
                          ] as string,
                        })
                      );
                    },
                  },
                  {
                    title: 'Update plans',
                    permission:
                      SuperAdminSystemPermissions.contentEntities.plans,
                    endpoint: '',
                    type: 'request',
                    action: () => {
                      setplansModalState({
                        selected: row[1] as string,
                        open: true,
                      });
                    },
                  },
                  {
                    title: 'Publish All',
                    permission:
                      SuperAdminSystemPermissions.contentEntities.publishAll,
                    endpoint: '',
                    type: 'request',
                    action: () => {
                      setMultiplePublish({
                        selectedId: row[1] as string,
                        open: true,
                      });
                    },
                  },
                  {
                    title: 'Delete',
                    permission:
                      SuperAdminSystemPermissions.contentEntities.delete,
                    endpoint: '',
                    type: 'request',
                    action: () => {
                      DeleteItemAlert(async () => {
                        const res = await contentDispatch(
                          ContentEntityActions.deleteContentEntityAsync(
                            row[1] as string
                          )
                        );
                        if (res.meta.requestStatus === 'fulfilled') {
                          getData();
                        }
                      }, 'سوف يتم حذف  ' + row[8]);
                    },
                  },
                ];
              }}
              columnsSelectors={(r) => {
                return {
                  '8': (
                    <Link to={`/content/type/${type}/${r[1]}/0`}>
                      <div className="text-[#38A7B4] font-medium">{r[8]}</div>
                    </Link>
                  ),
                };
              }}
              extraActions={[
                {
                  label: 'Export as CSV',
                  onClick: () => {
                    onExport();
                  },
                  Icon: <DownloadIcon className="h-4 w-4" />,
                },
              ]}
              preferences={tablePreferances || undefined}
              setPreferences={setTablePreferences}
            />
          ) : (
            <div className="w-full h-full"></div>
          )}
          {authorModalState.open && (
            <AssignAuthorsModal
              isOpen={authorModalState.open}
              contentId={authorModalState.selected}
              onClose={() => {
                setAuthorModalState({ selected: '', open: false });
              }}
            />
          )}
          {plansModalState.open && (
            <AssignPlansModal
              loading={updatePlansStatus === 'loading'}
              contentEntityId={plansModalState.selected}
              isOpen={plansModalState.open}
              onClose={() => {
                setplansModalState({ selected: '', open: false });
                contentDispatch(ContentEntityActions.clearDetails());
                contentDispatch(PlansActions.clear());
              }}
              onSubmit={async (data) => {
                const res = await contentDispatch(
                  ContentEntityActions.updateContentPlansAsync({
                    contentEntityId: plansModalState.selected,
                    planIds: data || [],
                  })
                );
                if (res.meta.requestStatus === 'fulfilled') {
                  setplansModalState({ selected: '', open: false });
                  contentDispatch(ContentEntityActions.clearDetails());
                  contentDispatch(PlansActions.clear());
                }
              }}
            />
          )}
          {multiplePublishModalState.open && (
            <PublishAllCollectionComponentsModal
              isOpen={multiplePublishModalState.open}
              onClose={() => {
                setMultiplePublish({ selectedId: '', open: false });
              }}
              loading={multiblePubliLoading}
              contentEntityId={multiplePublishModalState.selectedId}
              onSubmit={async (data) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                //
                const res =
                  data &&
                  (await contentDispatch(
                    ContentEntityActions.multiblePublishAsync({
                      languageId: 1,
                      contentEntityPublishedStateInputs: [
                        {
                          contentEntityId: multiplePublishModalState.selectedId,
                          published: true,
                        },
                      ].concat([], ...(Object.values(data) as any)),
                    })
                  ));
                if (res?.payload) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  const hasError = await res.payload.find(
                    (item: { successful: boolean; message: string }) =>
                      !item.successful
                  );
                  if (hasError) {
                    if (hasError.successful === false) {
                      commonDispatch(
                        CommonActions.setAlert({
                          type: AlertType.ERROR,
                          messages: [
                            {
                              message: hasError.message,
                              code: 400,
                              field: '',
                            },
                          ],
                        })
                      );
                    }
                  } else {
                    setMultiplePublish({ selectedId: '', open: false });
                    commonDispatch(
                      CommonActions.setAlert({
                        type: AlertType.SUCCESS,
                        messages: [
                          {
                            message: 'Content Published Successfully',
                            code: 400,
                            field: '',
                          },
                        ],
                      })
                    );
                    getData();
                  }
                }
              }}
            />
          )}
        </div>
      </PageLayoutsContainer>
    </div>
  );
};

export default ContentEntitiesPage;
